/**
 * Context: BrandingContext
 * SpecReferenceID: CONFIG-V1
 * Requirements: CONFIG-V1-R13, CONFIG-V1-R14, CONFIG-V1-R16
 *
 * Provides client branding configuration to the entire application.
 * CONFIG-V1-R13: Client branding configuration (app name, logo, favicon, colors).
 * CONFIG-V1-R14: Configurable disclaimer/legal text per deployment.
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 *
 * Risk Links: R11
 * FMEA Links: FM11
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import type { BrandingConfig, BrandingConfigApiResponse } from '@/types';
import api from '@/lib/api';
import { useIsAuthenticated } from '@/stores/auth-store';

/**
 * State shape for the branding context
 */
interface BrandingState {
  /** Branding configuration from backend */
  branding: BrandingConfig;
  /** Whether branding is currently being loaded */
  isLoading: boolean;
  /** Error message if branding fetch failed */
  error: string | null;
  /**
   * ADMIN-V1-R13 (Bug #5384): True once a branding fetch has SUCCEEDED at
   * least once in this app session.
   *
   * Consumers that write branding back to the server (the admin Configuration
   * page) MUST check this before saving. Without it, `branding` is
   * indistinguishable from a real all-null config when a fetch has failed --
   * and saving from that state nulls every field in client_branding.
   */
  hasLoadedOnce: boolean;
  /** Re-fetch branding from backend */
  refetch: () => Promise<void>;
}

/**
 * Default branding - current hardcoded values as fallback.
 */
const DEFAULT_BRANDING: BrandingConfig = {
  appName: 'Galen Portal',
  logoUrl: null,
  logoDarkUrl: null,
  logoAltText: 'Galen Portal',
  faviconUrl: null,
  colors: {},
  darkColors: {},
  disclaimers: {},
  citationDisplayMode: 'inline',
  corColorMappings: [],
  loeColorMappings: [],
  chatSystemPrompt: null,
  chatUngroundedFallbackMessage: null,
  galenSearchSystemPrompt: null,
  galenSearchUngroundedFallbackMessage: null,
  chatTemperature: null,
  chatMaxTokens: null,
  chatTopP: null,
  chatGroundingTopN: null,
  chatSemanticConfiguration: null,
  chatScoringProfile: null,
  chatPastMessagesIncluded: null,
  galenSearchTemperature: null,
  galenSearchMaxTokens: null,
  galenSearchTopP: null,
  galenSearchGroundingTopN: null,
  searchHybridTopN: null,
  galenSearchSemanticConfiguration: null,
  galenSearchScoringProfile: null,
  galenSearchPastMessagesIncluded: null,
  searchSemanticTopN: null,
  searchSimpleTopN: null,
  searchDefaultMode: null,
  semanticSearchDefaultMode: null,
  galenSearchDefaultMode: null,
  searchSemanticConfiguration: null,
  searchVectorK: null,
  searchVectorExhaustive: null,
  queryEmbeddingCacheTtlMinutes: null,
  searchVectorProfile: null,
  chatUseGrounding: null,
  reportingClassificationSystemPrompt: null,
  reportingClassificationTaxonomy: null,
};

/**
 * Transform backend snake_case response to frontend camelCase.
 */
function transformApiResponse(response: BrandingConfigApiResponse): BrandingConfig {
  const { config } = response;
  return {
    appName: config.app_name,
    logoUrl: config.logo_url,
    logoDarkUrl: config.logo_dark_url,
    logoAltText: config.logo_alt_text,
    faviconUrl: config.favicon_url,
    colors: {
      background: config.colors?.background,
      foreground: config.colors?.foreground,
      primary: config.colors?.primary,
      primaryForeground: config.colors?.primary_foreground,
      secondary: config.colors?.secondary,
      secondaryForeground: config.colors?.secondary_foreground,
      accent: config.colors?.accent,
      accentForeground: config.colors?.accent_foreground,
    },
    darkColors: {
      background: config.dark_colors?.background,
      foreground: config.dark_colors?.foreground,
      primary: config.dark_colors?.primary,
      primaryForeground: config.dark_colors?.primary_foreground,
      secondary: config.dark_colors?.secondary,
      secondaryForeground: config.dark_colors?.secondary_foreground,
      accent: config.dark_colors?.accent,
      accentForeground: config.dark_colors?.accent_foreground,
    },
    disclaimers: {
      safetyDisclaimer: config.disclaimers?.safety_disclaimer,
      responseDisclaimer: config.disclaimers?.response_disclaimer,
      footerText: config.disclaimers?.footer_text,
      footerRegulatory: config.disclaimers?.footer_regulatory,
    },
    citationDisplayMode: config.citation_display_mode ?? 'inline',
    corColorMappings: config.cor_color_mappings ?? [],
    loeColorMappings: config.loe_color_mappings ?? [],
    chatSystemPrompt: config.chat_system_prompt ?? null,
    chatUngroundedFallbackMessage: config.chat_ungrounded_fallback_message ?? null,
    galenSearchSystemPrompt: config.galen_search_system_prompt ?? null,
    galenSearchUngroundedFallbackMessage: config.galen_search_ungrounded_fallback_message ?? null,
    chatTemperature: config.chat_temperature ?? null,
    chatMaxTokens: config.chat_max_tokens ?? null,
    chatTopP: config.chat_top_p ?? null,
    chatGroundingTopN: config.chat_grounding_top_n ?? null,
    chatSemanticConfiguration: config.chat_semantic_configuration ?? null,
    chatScoringProfile: config.chat_scoring_profile ?? null,
    chatPastMessagesIncluded: config.chat_past_messages_included ?? null,
    galenSearchTemperature: config.galen_search_temperature ?? null,
    galenSearchMaxTokens: config.galen_search_max_tokens ?? null,
    galenSearchTopP: config.galen_search_top_p ?? null,
    galenSearchGroundingTopN: config.galen_search_grounding_top_n ?? null,
    searchHybridTopN: config.search_hybrid_top_n ?? null,
    galenSearchSemanticConfiguration: config.galen_search_semantic_configuration ?? null,
    galenSearchScoringProfile: config.galen_search_scoring_profile ?? null,
    galenSearchPastMessagesIncluded: config.galen_search_past_messages_included ?? null,
    searchSemanticTopN: config.search_semantic_top_n ?? null,
    searchSimpleTopN: config.search_simple_top_n ?? null,
    searchDefaultMode: config.search_default_mode ?? null,
    semanticSearchDefaultMode: config.semantic_search_default_mode ?? null,
    galenSearchDefaultMode: config.galen_search_default_mode ?? null,
    searchSemanticConfiguration: config.search_semantic_configuration ?? null,
    searchVectorK: config.search_vector_k ?? null,
    searchVectorExhaustive: config.search_vector_exhaustive ?? null,
    queryEmbeddingCacheTtlMinutes: config.query_embedding_cache_ttl_minutes ?? null,
    searchVectorProfile: config.search_vector_profile ?? null,
    chatUseGrounding: config.chat_use_grounding ?? null,
    reportingClassificationSystemPrompt:
      config.reporting_classification_system_prompt ?? null,
    reportingClassificationTaxonomy: config.reporting_classification_taxonomy
      ? {
          questionTypes: config.reporting_classification_taxonomy.question_types,
          tones: config.reporting_classification_taxonomy.tones,
          topicAreas: config.reporting_classification_taxonomy.topic_areas,
        }
      : null,
  };
}

/**
 * CSS variable name mapping for branding colors.
 */
const COLOR_CSS_MAP: Record<string, string> = {
  background: '--background',
  foreground: '--foreground',
  primary: '--primary',
  primaryForeground: '--primary-foreground',
  secondary: '--secondary',
  secondaryForeground: '--secondary-foreground',
  accent: '--accent',
  accentForeground: '--accent-foreground',
};

/**
 * Apply light mode CSS variable overrides to document root.
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 */
function applyLightColorOverrides(colors: BrandingConfig['colors']): void {
  const root = document.documentElement.style;
  for (const [key, cssVar] of Object.entries(COLOR_CSS_MAP)) {
    const value = colors[key as keyof typeof colors];
    if (value) {
      root.setProperty(cssVar, value);
    } else {
      root.removeProperty(cssVar);
    }
  }
}

/**
 * Apply dark mode CSS variable overrides via injected style tag.
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 */
function applyDarkColorOverrides(darkColors: BrandingConfig['darkColors']): void {
  const styleId = 'branding-dark-overrides';
  let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;

  const overrides: string[] = [];
  for (const [key, cssVar] of Object.entries(COLOR_CSS_MAP)) {
    const value = darkColors[key as keyof typeof darkColors];
    if (value) {
      overrides.push(`  ${cssVar}: ${value};`);
    }
  }

  if (overrides.length === 0) {
    // Remove style tag if no overrides
    if (styleEl) {
      styleEl.remove();
    }
    return;
  }

  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = styleId;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `.dark {\n${overrides.join('\n')}\n}`;
}

/**
 * Update document title.
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 */
function updateDocumentTitle(appName: string): void {
  document.title = appName;
}

/**
 * Update favicon link element.
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 */
function updateFavicon(faviconUrl: string | null | undefined): void {
  if (!faviconUrl) return;

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
}

/**
 * Branding context
 */
const BrandingContext = createContext<BrandingState | undefined>(undefined);

/**
 * Props for BrandingProvider component
 */
interface BrandingProviderProps {
  children: React.ReactNode;
}

/**
 * BrandingProvider component
 *
 * Fetches branding configuration from the backend API and provides it
 * to child components. Applies CSS variable overrides, document title,
 * and favicon on load.
 *
 * CONFIG-V1-R16: Apply branding configuration to frontend UI at runtime.
 */
export function BrandingProvider({ children }: BrandingProviderProps): React.ReactElement {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // ADMIN-V1-R13 (Bug #5384): tracks whether a fetch has ever SUCCEEDED.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const wasAuthenticated = useRef(isAuthenticated);

  const fetchBranding = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await api.get<BrandingConfigApiResponse>('/config/branding');
      const transformed = transformApiResponse(response.data);
      setBranding(transformed);
      setHasLoadedOnce(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load branding';
      setError(errorMessage);
      console.error('[BrandingProvider] Failed to fetch branding:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch branding on mount
  useEffect(() => {
    void fetchBranding();
  }, [fetchBranding]);

  // ADMIN-V1-R13 (Bug #5384): Re-fetch when auth transitions to authenticated.
  //
  // BrandingProvider is mounted ABOVE AppInitializer in App.tsx, and React runs
  // parent effects before child effects, so the mount fetch above can fire
  // before the session is established. `fetchBranding` is useCallback(..., []),
  // a stable identity, so the mount effect runs exactly once and never retries
  // -- leaving `branding` stuck on DEFAULT_BRANDING (all-null) for the rest of
  // the app session.
  //
  // The dominant real-world path is in-session login: LoginPage calls
  // navigate() rather than reloading, so the provider is never remounted and
  // the branding fetched while logged out (or never fetched at all) is never
  // replaced. LoginPage already does exactly this for feature flags
  // (`void refetchFlags()` after login, so gated nav items appear immediately);
  // branding had the same gap but was never given the same treatment.
  //
  // Subscribing to auth state here rather than adding another call at the
  // LoginPage call site also covers SignInTokenPage (SAML handoff sets the
  // token mid-session) and any future auth entry point, instead of relying on
  // every such site remembering to refetch.
  useEffect(() => {
    const justAuthenticated = isAuthenticated && !wasAuthenticated.current;
    wasAuthenticated.current = isAuthenticated;
    if (justAuthenticated) {
      void fetchBranding();
    }
  }, [isAuthenticated, fetchBranding]);

  // CONFIG-V1-R16: Apply side effects when branding changes
  useEffect(() => {
    applyLightColorOverrides(branding.colors);
    applyDarkColorOverrides(branding.darkColors);
    updateDocumentTitle(branding.appName);
    updateFavicon(branding.faviconUrl);
  }, [branding]);

  const value: BrandingState = {
    branding,
    isLoading,
    error,
    hasLoadedOnce,
    refetch: fetchBranding,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Hook to access branding configuration from context.
 *
 * @returns Branding state including config, loading state, and error
 * @throws Error if used outside BrandingProvider
 */
export function useBranding(): BrandingState {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}
